import rollbar from '~/vendor/rollbar';
import {createAction} from 'redux-actions';
import remoteServices from '~/constants/RemoteServices';
import {checkResponseOkay, getHeaders, postHeaders} from '~/utils/http';
import {buildUrl, updateCompositeIdInUrl} from '~/utils/url';
// serializers
import {
  printMinimumSerialize,
  quoteServiceSerialize
} from '~/data/serializers/fundraising';
import {serializeForFundraising} from '~/data/serializers/railsFrontend';
// selectors
import {isDirty} from '~/selectors/DesignSelectors';
import {getCurrentDesignId, isBlank} from '~/selectors/CanvasSelectors';
import {
  getColorSelections,
  getStyleIdFromSelection
} from '~/selectors/ProductSelectors';
import {
  getSuggestedPriceForStyle,
  salePricesJS
} from '~/selectors/FundraisingSelectors';
// types
import FundraisingTypes from '~/constants/types/FundraisingTypes';
// Constants
import {AUTOSAVE_EMAIL} from '~/constants/FundraisingConstants';
import ApiConstants from '~/constants/ApiConstants';

export const fetchData = createAction(FundraisingTypes.FETCH);
export const setSkipOverview = createAction(FundraisingTypes.SET_SKIP_OVERVIEW);
export const setDefaultSalePricesAction = () => setDefaultSalePrices;
export const updateProductsProfitAction = () => updateProductsProfit;

const designSaveSuccess = createAction(FundraisingTypes.SAVE_SUCCESS);
const designSaveFailed = createAction(FundraisingTypes.SAVE_FAILED);
const fetchSuccess = createAction(FundraisingTypes.FETCH_SUCCESS);
const fetchFailed = createAction(FundraisingTypes.FETCH_FAILED);
const saveDesign = createAction(FundraisingTypes.SAVE_DESIGN);
const updateEstimatedProfit = createAction(
  FundraisingTypes.UPDATE_ESTIMATED_PROFIT,
  (minProfit, maxProfit) => {
    return {minProfit, maxProfit};
  }
);
const updateGoalQuantity = createAction(FundraisingTypes.UPDATE_GOAL_QUANTITY);
const updateProfit = createAction(FundraisingTypes.UPDATE_PROFIT);
const updateSalePrice = createAction(FundraisingTypes.UPDATE_SALE_PRICE);

export const addProductToFundraising = product => (dispatch, getState) => {
  const state = getState();
  const suggestedPrice = getSuggestedPriceForStyle(state, product.styleId);
  const payload = {colorId: product.colorId, price: suggestedPrice};
  dispatch(updateSalePrice(payload));
  return updateProductPrintMinimum(product.colorId, dispatch, getState);
};

export const changeGoalQuantity = quantity => (dispatch, getState) => {
  dispatch(updateGoalQuantity(quantity));
  return updateProductsProfit(dispatch, getState);
};

export const incrementSalePrice = salePrice => (dispatch, getState) => {
  const payload = {colorId: salePrice.colorId, price: salePrice.price + 1};
  dispatch(updateSalePrice(payload));
  updateProductPrintMinimum(salePrice.colorId, dispatch, getState);
  return updateProductsProfit(dispatch, getState);
};

export const decrementSalePrice = salePrice => (dispatch, getState) => {
  const payload = {colorId: salePrice.colorId, price: salePrice.price - 1};
  dispatch(updateSalePrice(payload));
  updateProductPrintMinimum(salePrice.colorId, dispatch, getState);
  return updateProductsProfit(dispatch, getState);
};

export const saveFundraiser = () => async (dispatch, getState) => {
  const state = getState();

  if (!isDirty(state)) {
    return new Promise(res => res(true));
  }

  const saveData = serializeForFundraising(state);

  const queryParams = {
    api_version: ApiConstants.designs.version
  };

  const url = buildUrl(`${remoteServices.designs}/api/designs/`, queryParams);

  dispatch(saveDesign());

  return window
    .fetch(url, {...postHeaders, body: saveData})
    .then(checkResponseOkay)
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        const compositeId = data.design.composite_id;
        const designName = data.design.design_name;
        updateCompositeIdInUrl(compositeId);
        dispatch(
          designSaveSuccess({
            designId: getCurrentDesignId(state),
            designName: designName,
            email: AUTOSAVE_EMAIL,
            response: data
          })
        );
        return true;
      } else {
        throw new Error('Design saving failed');
      }
    })
    .catch(error => {
      rollbar.critical('Save Failure!', error, {saveData});
      dispatch(
        designSaveFailed({
          designName: undefined,
          email: AUTOSAVE_EMAIL,
          error: {
            errors: {
              base: ['server_error'],
              errorMessage: ['Server could not process request.']
            }
          }
        })
      );
      return false;
    });
};

export const updateProductsPrintMinimum = () => (dispatch, getState) => {
  const state = getState();
  const product_ids = getColorSelections(state);
  product_ids.map(productId =>
    updateProductPrintMinimum(productId, dispatch, getState)
  );
};

function updatePrintMinimum(productId, dispatch) {
  return data => {
    const payload = {
      colorId: productId.toString(),
      printMinimumQuantity: data.minimum,
      errorMessage: data.errorMessage ? data.errorMessage : null
    };
    dispatch({type: FundraisingTypes.UPDATE_PRINT_MINIMUM, payload: payload});
  };
}

function updateProfits(dispatch) {
  return data => {
    data.quotes.forEach(q => {
      const payload = {colorId: q.product_id.toString(), profit: q.profit};
      dispatch(updateProfit(payload));
    });

    const profits = data.quotes.map(q => q.profit).sort((a, b) => a - b);
    dispatch(updateEstimatedProfit(profits[0], profits[profits.length - 1]));
    dispatch(fetchSuccess());
  };
}

function updateProductsProfit(dispatch, getState) {
  const state = getState();
  const blankDesign = isBlank(state);
  if (blankDesign) {
    dispatch(updateEstimatedProfit('0', '0'));
    dispatch(fetchSuccess());
  } else {
    const postBody = quoteServiceSerialize(state);
    return window
      .fetch(remoteServices.fundraising_quote, {...postHeaders, body: postBody})
      .then(checkResponseOkay)
      .then(response => response.json())
      .then(updateProfits(dispatch))
      .catch(error => dispatch(fetchFailed(error)));
  }
}

export function updateProductPrintMinimum(productId, dispatch, getState) {
  const state = getState();
  const blankDesign = isBlank(state);
  if (blankDesign) {
    return Promise.resolve(
      updatePrintMinimum(productId, dispatch)({minimum: 0})
    );
  } else {
    const printMinParams = printMinimumSerialize(productId, state);
    const url = buildUrl(
      remoteServices.fundraising_print_minimum,
      printMinParams
    );
    return window
      .fetch(url, {...getHeaders})
      .then(response => checkResponseOkay(response, true))
      .then(response => response.json())
      .then(updatePrintMinimum(productId, dispatch))
      .catch(error => {
        if (error.error) {
          return updatePrintMinimum(
            productId,
            dispatch
          )({
            errorMessage: error.error
          });
        } else {
          dispatch(fetchFailed(error));
        }
      });
  }
}

function setDefaultSalePrices(dispatch, getState) {
  const state = getState();
  const salePrices = salePricesJS(state);
  Object.keys(salePrices).forEach(productId => {
    if (salePrices[productId] === 0) {
      const styleId = getStyleIdFromSelection(state, {colorId: productId});
      if (styleId) {
        const suggestedPrice = getSuggestedPriceForStyle(state, styleId);
        const payload = {colorId: productId, price: suggestedPrice};
        dispatch(updateSalePrice(payload));
      }
    }
  });
  return new Promise(res => res(true));
}
